import { Injectable, signal } from '@angular/core';
import { useStore } from 'ngx-unificator/store';
import { SleighDataItem, Xmas, XmasPage, XmasStoreEntity } from '../../../core/store/xmas.store';
import { GlobalStoreName } from '../../../core/store';
import { ALREADY_SPINED_SLOT_STR, BasePromo, EventType } from '../base-promo';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { GameCategory } from '../../../core/services/games/game-category';
import { ITournament } from '../../tournaments/tournaments.interface';

const BONUS_IDS: string[] = ['promo_spins_1_2025', 'promo_spins_2_2025', 'promo_spins_3_2025'];

const DAILY_BONUS_MISSION = 'ID1192';
export enum XmasAction {
  GO_TO_DEPOSIT = 'deposit',
  TOURNAMENTS = 'tournaments',
  PROMO_AUTH = 'promo-auth',
  GET_BONUS = 'get-bonus',
  GO_TO_BONUSES = 'bonuses',
}

@Injectable({
  providedIn: 'root',
})
export class XmasService extends BasePromo {

  public xmasStore = useStore<XmasStoreEntity>(GlobalStoreName.XMAS_STORE);

  public eventName = 'xmas';

  /**
   * Game list config
   */
  public gamesListConfig = {
    filter: {
      category: GameCategory.WINTER_FAVORITES,
      limit: 5,
    },
    hideNav: true,
    hideTitle: true,
    alignWithOutsideContainer: true,
  };

  public currentDate = signal(null);

  /**
   * Action rotes config
   */
  public actionRoutesConfig = {
    [XmasAction.GET_BONUS]: {
      key: null,
      url: '/profile/account',
      authUrl: ['profile', 'account'],
    },
    [XmasAction.GO_TO_BONUSES]: {
      key: null,
      url: '/bonuses',
      authUrl: ['bonuses'],
    },
    [XmasAction.TOURNAMENTS]: {
      key: 'promo-tournaments',
      url: '/tournaments',
      authUrl: ['tournaments'],
    },
    [XmasAction.GO_TO_DEPOSIT]: {
      key: 'promo-deposit',
      url: '/profile/deposit',
      authUrl: ['profile', 'deposit'],
    },
    [XmasAction.PROMO_AUTH]: {
      key: 'promo-auth',
      url: '/promo/xmas',
      authUrl: null,
    },
  };

  /**
   * Tournaments list
   */
  public tournaments$: Observable<ITournament[]> = this.getTournaments$('xmas');

  public promoDisabled$ = new BehaviorSubject<boolean>(false);

  get store(): Xmas {
    return this.xmasStore.storeSignal();
  }

  public initPromo$(): Observable<XmasPage> {
    this.patchXmasStore({ isLoadedPromo: false });
    return this.fetchPromoData$().pipe(
      tap(([page]) => this.checkIsPromoEnabled(page, this.promoDisabled$)),
      filter(data => !!data[0]),
      filter(data => !this.isPromoError(data[0])),
      map(([promo, lootbox, fs, bonuses]) => this._processPromoData$(promo, lootbox, fs, bonuses)),
      map(([promo, lootbox, fs, bonuses]) => this._processAdditionalPromoData$(promo, lootbox, fs, bonuses)),
      tap(([promo, lootboxes, freespins, bonuses]) => {
        this._updatePageStore(promo);

        if (!promo.isDisablePromo) {
          this.initWelcomePromoModal();
        }

        this.updateCurrentBonus(lootboxes, freespins, bonuses, BONUS_IDS, this.xmasStore);
        this.updateParticipateInPromo(lootboxes, BONUS_IDS, this.xmasStore);

        const isWeeklyMissionsCompleted = this.group.isExistGroup(DAILY_BONUS_MISSION);

        this.currentDate.set(this._resolveTodayForFinished());

        this.patchXmasStore({isWeeklyMissionsCompleted, isLoadedPromo: true});
      }),
    );
  };

  public onSpinBonus(isSpinBonus: boolean) {
    this.patchXmasStore({ isSpinBonus });
  }

  public onActivateBonus() {
    this.onActivateBonus$(this.xmasStore).subscribe(() => {
      this.onSpinBonus(false);
    });
  }

  public onClearBonus() {
    this.patchXmasStore({ currentPrize: null, currentBonus: null });
  }

  private _updatePageStore(promo: XmasPage) {
    if (promo.isDisablePromo) {
      this.patchXmasStore({ page: promo });
      return;
    }

    const page = {
      ...promo,
      HowItWorksSteps: [...Object.values(promo?.HowWorksSteps)],
      unpublishAt: promo?.unpublishAt,
      publishAt: promo.publishAt,
    };

    this.patchXmasStore({
      page,
      sleighData: page?.SleighData,
      currentSleighItem: page?.SleighData.find(item => item.currentDayIndex),
      currentSleighItemIndex: page?.SleighData.findIndex(item => item.currentDayIndex),
    });
  }

  private _processPromoData$(promo: XmasPage[], lootbox: any[], fs: any[], bonuses: any[]): any {
    return [this._resolveByRangeMonthDate(promo), lootbox, fs, bonuses];
  };

  private _processAdditionalPromoData$(promo: XmasPage[], lootbox: any[], fs: any[], bonuses: any[]): any {
    return [promo?.map(promoItem => ({
      ...promoItem,
      SleighData: this._resolveSleigh(promoItem?.SleighData, fs, lootbox),
      HowWorksSteps: [...Object.values(promoItem?.HowWorksSteps || {})],
      unpublishAt: this.time.toLocalDate(promoItem?.unpublishAt),
      publishAt: this.time.toLocalDate(promoItem?.publishAt),
    }))[0],
      lootbox,
      fs,
      bonuses,
    ];
  };

  private _resolveByRangeMonthDate(promo: XmasPage[]): XmasPage[] {
    const startDate = new Date(promo?.[0]?.StartPromoDate);
    const monthDates = this._getRangeOfMonthDates(startDate);

    return [
      {
        ...promo?.[0],
        SleighData: monthDates.map((date, i) => ({
          date,
          short_date: this._formatShortDate(date),
        })),
      },
    ];
  }


  private _resolveSleigh(sleigh: SleighDataItem[], fs: any[], lootbox: any[]): any[] {

    return sleigh.map((sleighItem: SleighDataItem, i: number) => {

      const date = new Date(sleighItem?.date);
      const formattedDate = date?.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
      }).replace(/\//g, '.');

      return {
        ...sleighItem,
        locked: this._resolveLockedState(sleighItem?.date),
        currentDayIndex: this._currentIndexByCurrentDay(sleigh) === i,
        date: formattedDate,
      };
    });
  }

  private _resolveTodayForFinished() {
    return this._formatShortDate(new Date());
  }

  public readQueryParamsDepositSuccess(destroy$: Subject<void>) {
    this.route.queryParams.pipe(
      filter(({ deposit_success }) => !!deposit_success),
      tap(() => this.cookie.delete(ALREADY_SPINED_SLOT_STR)),
      takeUntil(destroy$),
    ).subscribe();
  }

  private _resolveLockedState(date: string): boolean {
    const currentDate = new Date();
    const currentDateUTC = currentDate.toISOString();
    const originalDate = new Date(currentDateUTC);
    const convertedDateStr =
      originalDate.toISOString().split('T')[0] + ' 00:00:00.000000';
    return date > convertedDateStr;
  }

  private _currentIndexByCurrentDay(list: any[]): number {
    if (list) {
      const todayUTC = new Date().toISOString().split('T')[0];
      return list.findIndex(item => item.date.startsWith(todayUTC));
    }
  }

  private _formatShortDate(inputDate: any) {
    const date = new Date(inputDate);
    const monthNames = [
      't.jan', 't.feb', 't.mar', 't.apr', 't.may', 't.jun',
      't.jul', 't.aug', 't.sep', 't.oct', 't.nov', 't.dec'];

    const day = date.getDate().toString().padStart(2, '0');
    const month = monthNames[date.getMonth()];
    return `${day} ${this.translate.translate(month)}`;
  }

  private _getRangeOfMonthDates(startDate: Date): string[] {
    const year = startDate.getUTCFullYear();
    const month = startDate.getUTCMonth();

    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const dates: string[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(Date.UTC(year, month, day, 0, 0, 0));
      const formattedDate = date.toISOString().split('T')[0] + ' 00:00:00.000000';
      dates.push(formattedDate);
    }

    return dates;
  }

  public patchXmasStore(patch: Partial<XmasStoreEntity>) {
    this.xmasStore.storeSignal.update(store => ({ ...store, ...patch }));
  }

  public openHuntModal?(type: EventType): void {
    throw new Error('Method not implemented.');
  }

  async checkActivatedBonuses() {
    if (this.store.isWelcomeModalOpened) {
      return of(true);
    }

    if (!this.store.isUserAlreadyActivatedPromoLootbox && this.store.isLoadedPromo) {
      this.patchXmasStore({ isWelcomeModalOpened: true });
      const { XmasWelcomeModalComponent } = await import('../xmas/xmas-welcome-modal/xmas-welcome-modal.component');
      await this.modals.openLazy(XmasWelcomeModalComponent, { template: 'CLEAR' });
      const now = new Date();
      const day = now.getDay();
      const diff = 7 - day;
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + diff);
      endOfWeek.setHours(23, 59, 59, 999);
      this.cookie.set('welcome-modal-promo', 'true', endOfWeek, '/');
    }
    return of(true);
  }

}
